import { useCallback } from 'react';
import { debugLog, sendToTerminal } from '../../../lib/terminalLogger';
import { storage } from '../../../lib/storage/storage';
import { Keys } from '../../../lib/storage/keys';
import { savePedidoConfig, saveProductRules, saveUserPreferences, prepareSaveData } from '../utils/storage';
import { SaveValidationState } from '../types/saveValidation';
import { resolveSaveTargetItem } from '../../../lib/productionLinkage';

interface UseOrderActionsParams {
  role: string;
  selectedPedido: any;
  selectedItem: any;
  itemId?: string;
  openOnlyFlow?: boolean;
  productRules: any;
  contraction: string;
  hasMultipleContractions: boolean;
  delivery: string;
  selectedObjects: any[];
  totalObjectsWithQuantity: number;
  totalAreaWithQuantity: number;
  saveValidationState: SaveValidationState;
  saveValidationMessage: string | null;
  hasSaveErrors?: boolean;
  userInfo: any;
  setShowChecklist: (show: boolean) => void;
  setShowMonitorStatus: (show: boolean) => void;
  showMonitorStatus: boolean;
  startProductionExport: (exportType?: import('../../ExportModal/types').ExportType) => Promise<void>;
  setShowExportTypeSelector: (show: boolean) => void;
  onClose: () => void;
}

export interface UseOrderActionsReturn {
  handleAction: () => void;
  handleFinalSave: () => void;
  toggleMonitorStatus: () => void;
}

/**
 * Hook para gerenciar acoes principais do OrderDetails (salvar, exportar, etc)
 */
export function useOrderActions({
  role,
  selectedPedido,
  selectedItem,
  itemId,
  openOnlyFlow = false,
  productRules,
  contraction,
  hasMultipleContractions,
  delivery,
  selectedObjects,
  totalObjectsWithQuantity,
  totalAreaWithQuantity,
  saveValidationState,
  saveValidationMessage,
  hasSaveErrors = false,
  userInfo,
  setShowChecklist,
  setShowMonitorStatus,
  showMonitorStatus,
  startProductionExport,
  setShowExportTypeSelector,
  onClose
}: UseOrderActionsParams): UseOrderActionsReturn {
  const handleAction = useCallback(() => {
    sendToTerminal('[ACTION] handleAction clicado', {
      saveValidationPhase: saveValidationState.phase,
      saveValidationMessage,
      role,
      selectedObjectsCount: selectedObjects?.length ?? 0,
      totalObjectsWithQuantity,
      totalAreaWithQuantity,
      hasSelectedPedido: !!selectedPedido,
      hasSelectedItem: !!selectedItem
    });

    if (saveValidationState.phase !== 'valid') {
      sendToTerminal('[ACTION] Bloqueado por pre-validacao oficial', {
        phase: saveValidationState.phase,
        message: saveValidationMessage
      });
      return;
    }

    if (hasSaveErrors) {
      sendToTerminal('[ACTION] Bloqueado por validacao local de dimensoes/metragem', {
        hasSaveErrors,
      });
      return;
    }

    const proceed = async () => {
      sendToTerminal('[ACTION] proceed iniciado', { role });

      if (role === 'producao') {
        if (openOnlyFlow) {
          sendToTerminal('[ACTION] Fluxo producao open-only - abrindo checklist sem exportacao', {
            itemId,
            produto: selectedItem?.produto?.nome,
            isLinkedOperationalItem: (selectedItem as any)?.is_impressao_vinculada === true,
          });
        } else {
          sendToTerminal('[ACTION] Fluxo producao - mostrando modal de selecao de tipo');
          setShowExportTypeSelector(true);
          return;
        }
      }

      sendToTerminal('[ACTION] Preparando saveData', {
        hasSelectedPedido: !!selectedPedido,
        itemId,
        selectedObjectsCount: selectedObjects?.length ?? 0
      });

      const itemSelecionado = itemId
        ? (selectedPedido.itens.find((i: any) => i.oit_id?.toString?.() === itemId) || selectedPedido.itens[0])
        : selectedPedido.itens[0];

      // Item comercial (camiseta personalizada/sublimada) deve nomear/salvar o
      // arquivo no oit_id do item operacional de impressão vinculada. A lista
      // completa (__originalItems) preserva a impressão consumida na consolidação.
      const itensParaResolver = Array.isArray((selectedPedido as any).__originalItems)
        ? (selectedPedido as any).__originalItems
        : selectedPedido.itens;
      const itemCorreto = resolveSaveTargetItem(itemSelecionado, itensParaResolver);

      sendToTerminal('[ACTION] Item correto selecionado', {
        oit_id_selecionado: itemSelecionado?.oit_id,
        oit_id_operacional: itemCorreto?.oit_id,
        redirecionado: itemCorreto?.oit_id !== itemSelecionado?.oit_id,
        produto: itemCorreto?.produto?.nome,
        material: itemCorreto?.material?.nome
      });

      try {
        storage.set(Keys.export.selectedItemId, String(itemCorreto.oit_id), { area: 'session', ttlMs: 24 * 60 * 60 * 1000 });
      } catch {}

      const ostId = (selectedPedido as any)?.status_orcamento?.id ||
        (selectedPedido as any)?.ost_id ||
        (selectedPedido as any)?.orc_status_id ||
        (itemCorreto as any)?.ost_id ||
        (itemCorreto as any)?.status?.ost_id ||
        (itemCorreto as any)?.status?.id ||
        (selectedPedido as any)?.status?.ost_id ||
        (selectedPedido as any)?.status?.id ||
        undefined;

      const saveData = prepareSaveData({
        pedidoId: selectedPedido.orc_id,
        itemId: itemCorreto.oit_id,
        nomeArquivo: selectedPedido.nome_arquivo || '',
        cliente: {
          id: selectedPedido.cliente?.cli_id,
          nome: selectedPedido.cliente?.nome || '',
          nome_fantasia: selectedPedido.cliente?.nome_fantasia || ''
        },
        produto: {
          pro_id: Number(itemCorreto?.produto?.pro_id ?? 0) || undefined,
          nome: itemCorreto.produto.nome,
          tipo: (itemCorreto.produto?.tipo ?? '').toString()
        },
        // material pode ser null no item comercial (ex.: CAMISETA PERSONALIZADA —
        // o material vive na impressão vinculada). Fallback vazio: o naming de
        // arquivo já trata ausência com o placeholder "Material".
        material: itemCorreto.material?.nome ?? '',
        productRules,
        contraction,
        hasMultipleContractions,
        delivery,
        objects: selectedObjects,
        totalObjectsWithQuantity,
        totalAreaWithQuantity,
        metrosVendidos: itemCorreto.quantidade,
        usuario: userInfo?.login || userInfo?.nome || `user_${userInfo?.id || 'unknown'}`,
        ostId,
        oitStatusId: (itemCorreto as any)?.status?.oit_status_id ?? (itemCorreto as any)?.oit_status_id
      });

      storage.set(Keys.export.currentSaveData, saveData, { area: 'session', ttlMs: 24 * 60 * 60 * 1000 });

      sendToTerminal('[ACTION] Abrindo ChecklistWindow', {
        saveDataObjetos: saveData?.objetos?.length ?? 0,
        pedidoId: saveData?.pedido?.orc_id,
        itemId: saveData?.pedido?.oit_id
      });
      setShowChecklist(true);
    };

    sendToTerminal('[ACTION] Chamando proceed');
    proceed();
  }, [
    role,
    saveValidationState,
    saveValidationMessage,
    hasSaveErrors,
    itemId,
    openOnlyFlow,
    selectedPedido,
    selectedItem,
    productRules,
    contraction,
    hasMultipleContractions,
    delivery,
    selectedObjects,
    totalObjectsWithQuantity,
    totalAreaWithQuantity,
    userInfo,
    setShowChecklist,
    startProductionExport,
    setShowExportTypeSelector
  ]);

  const handleFinalSave = useCallback(() => {
    debugLog('ACTIONS:START', {
      action: 'salvar-configuracoes',
      source: 'WEBVIEW',
      refId: `${selectedPedido.orc_id}/${selectedItem.oit_id}`
    });

    if (productRules) {
      saveProductRules(
        selectedPedido.orc_id,
        selectedItem.oit_id,
        productRules
      );
    }

    savePedidoConfig(selectedPedido.orc_id, {
      contraction: hasMultipleContractions ? 'CV' : contraction,
      contractionDetails: hasMultipleContractions ? selectedObjects.map(obj => ({
        id: obj.id,
        nome: obj.nome,
        contraction: obj.contraction || '0px'
      })) : undefined,
      delivery,
      timestamp: new Date().toISOString()
    });

    saveUserPreferences(contraction, delivery);

    debugLog('ACTIONS:END', {
      action: 'salvar-configuracoes',
      status: 'success',
      refId: `${selectedPedido.orc_id}/${selectedItem.oit_id}`
    });

    onClose();
  }, [
    selectedPedido,
    selectedItem,
    productRules,
    contraction,
    hasMultipleContractions,
    selectedObjects,
    delivery,
    onClose
  ]);

  const toggleMonitorStatus = useCallback(() => {
    setShowMonitorStatus(!showMonitorStatus);
  }, [showMonitorStatus, setShowMonitorStatus]);

  return {
    handleAction,
    handleFinalSave,
    toggleMonitorStatus
  };
}
